/**
 * ClientApp/src/apps/components/mapViewers/tests/PieMap/source/testPieLayers.js
 *
 * Isolated sandbox for validating well clustering.
 *
 * TEST A — plain count cluster, known-working baseline.
 * TEST D — smart-mapping donut cluster using CURRENT_STATUS.
 *
 * Test D uses the confirmed-working ArcGIS pattern:
 * - up to 10 explicit raw CURRENT_STATUS values
 * - remaining values fall into "Other"
 * - UniqueValueRenderer is assigned before createRendererForClustering()
 *
 * Remove this folder once the smart-pie implementation is validated
 * and ported into the production layer pipeline.
 */
import { AppResource, APP_CONFIG } from "@config/config";

const WELL_LAYER_ALL_FIELDS = [
  "OBJECTID", "ID", "WELL_ID", "WELL_COMPL_ID", "WELL_PS_ID", "RESERVATION_ID",
  "API10", "API_NUMBER", "COMPLETION_DATE", "COMPLETION_SEQ",
  "CUM_DAYS_ON_INJECTION", "CUM_DAYS_ON_PRODUCTION", "CUM_GAS", "CUM_OIL", "CUM_WATER",
  "CUM_GASOIL_RATIO", "CUM_INJECTION_DATA", "CUM_INJECTION_GAS", "CUM_INJECTION_OIL",
  "CUM_INJECTION_WATER", "CURRENT_COMPL", "CURRENT_STATUS", "DATA_SOURCE",
  "DRILL_TOTAL_DEPTH", "ELEVATION", "FIELD_NAME", "FINAL_LAHEE_CLASS", "FINAL_WELL_TYPE",
  "FIRST_CURRENT_COMPL", "FIRST_DAYS_ON_INJECTION", "FIRST_DAYS_ON_PRODUCTION",
  "FIRST_GAS", "FIRST_OIL", "FIRST_WATER", "FIRST_GASOIL_RATIO", "FIRST_INJECTION_GAS",
  "FIRST_INJECTION_OIL", "FIRST_INJECTION_WATER", "FIRST_PRODUCTION_DATE",
  "FORMATION_AT_TD", "GAS_INJ_OPENING_CUM", "OIL_INJ_OPENING_CUM", "WATER_INJ_OPENING_CUM",
  "GAS_OPENING_CUM", "OIL_OPENING_CUM", "WATER_OPENING_CUM", "LAST_DAYS_ON_INJECTION",
  "LAST_DAYS_ON_PRODUCTION", "LAST_GAS", "LAST_OIL", "LAST_WATER", "LAST_GASOIL_RATIO",
  "LAST_INJECTION_GAS", "LAST_INJECTION_OIL", "LAST_INJECTION_WATER", "LAST_PRODUCTION_DATE",
  "LATITUDE_BH", "LATITUDE", "LEASE_NAME", "LONGITUDE_BH", "LONGITUDE", "LOWER_PERFORATION",
  "OPERATOR_NAME", "PROD_ID", "PRODUCING_ZONE", "UPPER_PERFORATION", "WELL_NUMBER",
  "SPUD_DATE", "DATE_CREATED", "DATE_UPDATED", "DATE_DELETED", "DELETED",
  "DATE_CREATED_WD", "DATE_UPDATED_WD", "DATE_DELETED_WD"
];

const WELL_URL =
  AppResource.VITE_NIOGEMS_GIS_URL + APP_CONFIG.MAPS.Well.layerId;

/**
 * Keep this at <= 10.
 *
 * Any CURRENT_STATUS not explicitly listed here is handled by
 * defaultSymbol/defaultLabel and appears as "Other".
 */
const SMART_PIE_CATEGORIES = [
  { value: "GAS",     label: "Active Gas",              color: "#1976D2" },
  { value: "OIL",     label: "Active Oil",              color: "#2E7D32" },
  { value: "CBM",     label: "Active Coalbed Methane",  color: "#00897B" },
  { value: "INJ",     label: "Active Injection",        color: "#00ACC1" },
  { value: "O&I",     label: "Active Oil & Injection",  color: "#43A047" },
  { value: "INA-GAS", label: "Inactive Gas",            color: "#F9A825" },
  { value: "INA-OIL", label: "Inactive Oil",            color: "#FB8C00" },
  { value: "ABD-GAS", label: "Plugged Gas",             color: "#C62828" },
  { value: "D&A",     label: "Plugged D&A",             color: "#6D4C41" },
];

export const TestPieLayers = {

  /* --------------------------------------------------------------
     A. SIMPLE COUNT — known-working baseline.
  -------------------------------------------------------------- */
  TEST_A_SIMPLE_COUNT: {
    url: WELL_URL,
    title: "TEST A — Simple Count Cluster",
    isExcluded: false,
    visible: false,
    outFields: WELL_LAYER_ALL_FIELDS,

    featureReduction: {
      type: "cluster",
      clusterRadius: "100px",
      clusterMinSize: "24px",
      clusterMaxSize: "60px",

      fields: [{
        name: "API_NUMBER_COUNT",
        alias: "API_NUMBER_COUNT",
        statisticType: "count",
        onStatisticField: "API_NUMBER"
      }],

      labelingInfo: [{
        labelExpressionInfo: {
          expression: "$feature.API_NUMBER_COUNT"
        },
        deconflictionStrategy: "none",
        labelPlacement: "center-center",
        symbol: {
          type: "text",
          color: "white",
          font: {
            size: "12px",
            weight: "bold"
          }
        }
      }],

      popupTemplate: {
        title: "Well Cluster",
        content: "This cluster contains {API_NUMBER_COUNT} wells.",
        fieldInfos: [{
          fieldName: "API_NUMBER_COUNT",
          format: {
            places: 0,
            digitSeparator: true
          }
        }]
      }
    }
  },

  /* --------------------------------------------------------------
     D. SMART PIE — confirmed-working approach.

     Uses the real well layer and CURRENT_STATUS.

     IMPORTANT:
     clusterPieSmartMapping.js creates a real UniqueValueRenderer,
     assigns it to layer.renderer, then calls
     createRendererForClustering().
  -------------------------------------------------------------- */
  TEST_D_SMART_PIE: {
    url: WELL_URL,
    title: "TEST D — Smart Pie (CURRENT_STATUS)",
    isExcluded: false,
    visible: false,
    opacity: 1,
    outFields: WELL_LAYER_ALL_FIELDS,
    maxScale: 0,
    popupEnabled: true,

    smartClusterPie: {
      fieldName: "CURRENT_STATUS",
      categories: SMART_PIE_CATEGORIES,

      otherLabel: "Other",
      otherColor: "#808080",

      shape: "donut",
      holePercentage: 0.66,
    },
  },

};